/**
 * Document configuration for passenger identification documents
 * This file defines the document types and their required/optional fields
 */

import { getNames, getCodes } from 'country-list';

// Get all country names and codes
const countryNames = getNames();
const countryCodes = getCodes();

// Create country options for dropdowns
export const COUNTRY_OPTIONS = countryNames.map((name, index) => ({
  value: countryCodes[index],
  label: name
}));

// Create nationality options (same as countries but with different labeling)
export const NATIONALITY_OPTIONS = countryNames.map((name, index) => ({
  value: countryCodes[index],
  label: name
}));

export const DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'national id', label: 'National ID' },
  { value: 'aadhar card', label: 'Aadhar Card' },
  { value: 'iqama', label: 'Iqama' },
  { value: 'visa', label: 'Visa' },
  { value: 'driving license', label: 'Driving License' },
  { value: 'voter id', label: 'Voter ID' },
  { value: 'pan card', label: 'PAN Card' }
];

/**
 * Field configuration for each document type
 * Each field has:
 * - name: field name (matches API field names)
 * - label: display label
 * - type: input type (text, date, select)
 * - required: whether the field is mandatory
 * - options: for select fields, array of {value, label} objects
 */
export const DOCUMENT_FIELD_CONFIG = {
  passport: [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: true },
    { name: 'nationality', label: 'Nationality', type: 'select', required: true, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  'national id': [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: true },
    { name: 'nationality', label: 'Nationality', type: 'select', required: true, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  'aadhar card': [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: false },
    { name: 'nationality', label: 'Nationality', type: 'select', required: false, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  iqama: [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: true },
    { name: 'nationality', label: 'Nationality', type: 'select', required: true, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  visa: [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: true },
    { name: 'nationality', label: 'Nationality', type: 'select', required: true, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: true },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  'driving license': [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: true },
    { name: 'nationality', label: 'Nationality', type: 'select', required: false, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  'voter id': [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: false },
    { name: 'nationality', label: 'Nationality', type: 'select', required: false, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ],
  'pan card': [
    { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ]},
    { name: 'dateOfBirth', label: 'Date Of Birth', type: 'date', required: true },
    { name: 'documentNumber', label: 'Number', type: 'text', required: true },
    { name: 'dateOfIssue', label: 'Date of Issue', type: 'date', required: false },
    { name: 'dateOfExpiry', label: 'Date of Expiry', type: 'date', required: false },
    { name: 'nationality', label: 'Nationality', type: 'select', required: false, options: NATIONALITY_OPTIONS },
    { name: 'placeOfIssue', label: 'Place of Issue', type: 'text', required: false },
    { name: 'countryOfResidence', label: 'Country of Residence', type: 'select', required: false, options: COUNTRY_OPTIONS }
  ]
};

/**
 * Get field configuration for a specific document type
 * @param {string} documentType - The document type
 * @returns {Array} Array of field configurations
 */
export const getFieldsForDocumentType = (documentType) => {
  return DOCUMENT_FIELD_CONFIG[documentType] || [];
};

/**
 * Check if a field is required for a specific document type
 * @param {string} documentType - The document type
 * @param {string} fieldName - The field name
 * @returns {boolean} Whether the field is required
 */
export const isFieldRequired = (documentType, fieldName) => {
  const fields = getFieldsForDocumentType(documentType);
  const field = fields.find(f => f.name === fieldName);
  return field ? field.required : false;
};
